import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, BookOpen, Code, FileText, Menu, X } from 'lucide-react';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const Dropdown = ({ name, items, children, icon: Icon }) => {
    const isOpen = openDropdown === name;
    let timeout = null;

    const handleMouseEnter = () => {
      if (timeout) clearTimeout(timeout);
      setOpenDropdown(name);
    };

    const handleMouseLeave = () => {
      timeout = setTimeout(() => setOpenDropdown(null), 200);
    };

    return (
      <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <button className="group flex items-center gap-1.5 text-[14px] font-medium text-[#1E293B] hover:text-[#3B82F6] transition-colors duration-200 px-3 py-2 relative">
          {children}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          <span className={`absolute bottom-0 left-0 right-0 h-0.5 bg-[#3B82F6] transform origin-left transition-transform duration-200 ${isOpen ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
        </button>
        {isOpen && (
          <div 
            className="absolute top-full left-0 mt-2 w-[280px] bg-white rounded-[10px] border border-[#E5E7EB] py-2 shadow-lg z-[100]"
            style={{
              animation: 'slideDown 200ms ease-out',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            {items.map((item, idx) => (
              <Link
                key={idx}
                to={item.path}
                onClick={() => setOpenDropdown(null)}
                className="group/item flex items-center justify-between px-4 py-2.5 text-[14px] font-medium text-[#475569] hover:bg-[#F8FAFC] hover:text-[#1E293B] transition-all duration-150 mx-1.5 rounded-lg"
              >
                <div className="flex items-center gap-2.5">
                  {item.icon && <item.icon className="w-4 h-4 text-[#94A3B8] group-hover/item:text-[#3B82F6] transition-colors" />}
                  <span>{item.label}</span>
                </div>
                {item.badge && (
                  <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${item.badgeColor}`}>
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  const jobReadyItems = [
    { 
      path: '/programs/system-network-engineer', 
      label: 'System & Network Engineer', 
      badge: 'Beginner', 
      badgeColor: 'bg-[#16A34A] text-white',
      icon: BookOpen
    },
    { 
      path: '/programs/devops-engineer', 
      label: 'Cloud & DevOps Engineer', 
      badge: 'Popular', 
      badgeColor: 'bg-[#2563EB] text-white',
      icon: Code
    }
  ];

  const resourcesItems = [
    { path: '/resources', label: 'All Resources', icon: FileText },
    { path: '/blog', label: 'Blog & Articles', icon: FileText },
    { path: '/bootcamps', label: 'Bootcamps', icon: BookOpen }
  ];

  const aiCoursesItems = [
    { path: '/courses/ai-career-launchpad', label: 'AI Career Launchpad', icon: Code },
    { path: '/programs/ai-engineer', label: 'AI Engineer Program', icon: Code },
    { path: '/courses/ai-bootcamp', label: 'AI Bootcamp', icon: BookOpen },
    { path: '/courses/ai-solution-architect', label: 'AI Solution Architect', icon: Code },
    { path: '/courses/product-management-ai', label: 'Product Management with AI', icon: Code }
  ];

  const techCareerItems = [
    { path: '/programs', label: 'All Programs', icon: BookOpen },
    { path: '/programs/cybersecurity-specialist', label: 'Cybersecurity Specialist', icon: Code },
    { path: '/programs/ai-engineer', label: 'AI Engineer', icon: Code }
  ];

  return (
    <>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
        .animate-pulse-gentle {
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>
      
      <header 
        className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB] h-14"
      >
        <nav className="max-w-[1400px] mx-auto px-6 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo + Tagline */}
            <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_program-curriculum/artifacts/5bzlurrn_new_logo.jpg" 
                alt="CareerLift" 
                className="h-8 w-auto object-contain"
                style={{ imageRendering: 'crisp-edges' }}
              />
            </Link>

            {/* Center Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              <Dropdown name="jobready" items={jobReadyItems}>
                Job Ready Programs
              </Dropdown>
              
              <Dropdown name="aicourses" items={aiCoursesItems}>
                AI Courses
              </Dropdown>
              
              <Dropdown name="techcareers" items={techCareerItems}>
                Tech Career Pathways
              </Dropdown>
              
              <Dropdown name="resources" items={resourcesItems}>
                Resources
              </Dropdown>
              
              <Link 
                to="/about" 
                className="group relative text-[14px] font-medium text-[#1E293B] hover:text-[#3B82F6] transition-colors duration-200 px-3 py-2"
              >
                About
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#3B82F6] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-200"></span>
              </Link>
            </div>

            {/* Right Side CTA Block */}
            <div className="hidden lg:flex items-center gap-6">
              {/* Free AI Career Assessment */}
              <Link 
                to="/ai-career-roadmap" 
                className="group relative text-[14px] font-medium text-[#3B82F6] hover:text-[#2563EB] transition-colors duration-200"
              >
                Free AI Career Assessment
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#3B82F6] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-200"></span>
              </Link>
              
              {/* Student Dashboard Button */}
              <Link 
                to="/dashboard/login" 
                className="px-4 py-2 rounded-lg border border-[#D1D5DB] bg-white text-[#1E293B] hover:bg-[#F9FAFB] font-medium text-[14px] transition-all duration-200"
              >
                Student Dashboard
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 rounded-lg hover:bg-[#F9FAFB] transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu Slide-out Panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-16 bg-white z-40 animate-slideIn">
            <div className="px-6 py-6 space-y-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">Job Ready Programs</p>
                {jobReadyItems.map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-[#F9FAFB] hover:bg-[#EFF6FF] transition-colors"
                  >
                    <span className="text-[15px] font-semibold text-[#0F172A]">{item.label}</span>
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${item.badgeColor}`}>
                      {item.badge}
                    </span>
                  </Link>
                ))}
              </div>
              
              <div className="space-y-2">
                <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">AI Courses</p>
                {aiCoursesItems.map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center px-4 py-3 rounded-lg bg-[#F9FAFB] hover:bg-[#EFF6FF] transition-colors"
                  >
                    <span className="text-[15px] font-semibold text-[#0F172A]">{item.label}</span>
                  </Link>
                ))}
              </div>
              
              <Link 
                to="/programs" 
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-[15px] font-semibold text-[#0F172A] hover:bg-[#F9FAFB] rounded-lg transition-colors"
              >
                Tech Career Pathways
              </Link>
              
              <Link 
                to="/resources" 
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-[15px] font-semibold text-[#0F172A] hover:bg-[#F9FAFB] rounded-lg transition-colors"
              >
                Resources
              </Link>
              
              <Link 
                to="/bootcamps" 
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-[15px] font-semibold text-[#0F172A] hover:bg-[#F9FAFB] rounded-lg transition-colors"
              >
                Bootcamps
              </Link>
              
              <Link 
                to="/about" 
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-[15px] font-semibold text-[#0F172A] hover:bg-[#F9FAFB] rounded-lg transition-colors"
              >
                About
              </Link>
              
              <div className="pt-4 space-y-3 border-t border-[#E5E7EB]">
                <Link 
                  to="/ai-career-roadmap" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-center rounded-lg bg-[#EFF6FF] text-[#2563EB] font-semibold"
                >
                  Free AI Career Assessment
                </Link>
                <Link 
                  to="/contact" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-center rounded-lg bg-[#2563EB] text-white font-semibold"
                >
                  Get Started
                </Link>
                <Link 
                  to="/dashboard/login" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-center rounded-lg border-2 border-[#E5E7EB] text-[#4B5563] font-semibold"
                >
                  Student Dashboard
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;